import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart, type UseMetricChartResult } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { formatWallClockWorded } from '@/utils/date';

import styles from './BehaviorRespirationView.module.scss';

interface BehaviorRespirationViewProps {
    chart: UseMetricChartResult<'line'>;
    isLoading: boolean;
    error?: unknown;
    /** Try again in the chart's error state. */
    retry?: RetryProps;
    dateFrom?: string;
    dateTo?: string;
    lastMeasurement?: string;
    onDateChange?: (data: DateRange) => void;
}

export default function BehaviorRespirationView(props: BehaviorRespirationViewProps) {
    const {
        chart,
        isLoading,
        error,
        retry,
        dateFrom = '',
        dateTo = '',
        lastMeasurement = '',
        onDateChange,
    } = props;
    const { t } = useTranslation('behaviorModule');
    return (
        <GridLayout contentLayout="1-fixed" secondColumnWidth={375}>
            <ContentBlock
                title={t('respirationView.title')}
                containerRef={chart.fullScreenContainerRef}
                headerActions={
                    <ChartHeaderActions
                        chartRef={chart.chartRef}
                        chartName="behavior-respiration-chart"
                        csvExport={false}
                        isFullScreen={chart.isFullScreen}
                        disabled={isLoading}
                        onFullScreenToggle={chart.handleFullScreenToggle}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateChange={onDateChange}
                        defaultPresetId="last7days"
                        show24hPreset={true}
                    />
                }>
                <MetricChart
                    legendRowLayout="centered"
                    kind="line"
                    data={chart.data}
                    options={chart.options}
                    legendItems={chart.legendItems}
                    visibleSeries={chart.visibleSeries}
                    onToggleSeries={chart.toggleSeries}
                    chartRef={chart.chartRef}
                    height={400}
                    isLoading={isLoading}
                    error={error}
                    onRetry={retry?.onRetry}
                    isRetrying={retry?.isRetrying}
                />
            </ContentBlock>
            <ContentBlock title={t('common.infoLabel')} titleIcon={<TitleIcon icon={Info} />}>
                <div className={styles.infoTextContainer}>{t('respirationView.infoText')}</div>
                <div>
                    <span className={styles.lastMeasurementLabel}>
                        <span>{t('common.lastMeasurement')}</span>
                        {isLoading ? (
                            <SkeletonBox width={100} height={24} borderRadius={8} />
                        ) : (
                            <span>{formatWallClockWorded(lastMeasurement)}</span>
                        )}
                    </span>
                </div>
            </ContentBlock>
        </GridLayout>
    );
}
